// What a viewer could still unlock, for the message under /Videos' Unlocked and Locked buttons
// (AccessMessage.vue; 2026-10-24, Seán), all from the fullstack's stats.json so that the figures
// are the ones #register and /Videos/Unlock show (fullstack buildVideoStats.js): signIn (Free
// videos a sign-in unlocks) and restricted (buckets of year and collections; a bucket is locked
// when its year is past the account's access in each of its collections). Hours are rounded per
// counted collection and added, as stats.json's "both", so every bucket gives its 66 hours.
const ACCESS_FIELDS = {masterclass: "masterclassVideosUntil", workshop: "workshopVideosUntil"};

const getUntil = (videoAccess, collection) => Number(videoAccess?.[ACCESS_FIELDS[collection]]) || 0;

function getIsLocked(bucket, videoAccess) {
  return bucket.collections.every((collection) => bucket.year > getUntil(videoAccess, collection));
}

function getTotal(buckets) {
  const seconds = {workshop: 0, masterclass: 0};
  for (const bucket of buckets) seconds[bucket.counted] += bucket.seconds;
  const hours = Math.round(seconds.workshop / 3600) + Math.round(seconds.masterclass / 3600);
  return {count: buckets.reduce((sum, bucket) => sum + bucket.videos, 0), hours};
}

// the event whose registration unlocks every one of these buckets: "workshop" or "masterclass"
// when the other alone wouldn't, else "" (both are needed, or each video is shared by the two
// and either would do)
function getRequiredEvent(buckets) {
  const needsWorkshop = buckets.some(({collections}) => !collections.includes("masterclass"));
  const needsMasterclass = buckets.some(({collections}) => !collections.includes("workshop"));
  if (needsWorkshop === needsMasterclass) return "";
  return needsWorkshop ? "workshop" : "masterclass";
}

// {free, restricted: {count, hours}, requiredEvent} for this account (signed out: no access, and
// the Free videos a sign-in unlocks), or null until stats.json (with its restricted buckets: a
// fullstack deploy ahead of the Frontend) has loaded
export function getLockedSummary(stats, {videoAccess, isSignedIn}) {
  if (!Array.isArray(stats?.restricted) || !stats.signIn) return null;
  const locked = stats.restricted.filter((bucket) => getIsLocked(bucket, videoAccess));
  const signIn = {count: stats.signIn.videos, hours: stats.signIn.hours};
  return {free: isSignedIn ? {count: 0, hours: 0} : signIn, restricted: getTotal(locked),
    requiredEvent: getRequiredEvent(locked)};
}

const getPlural = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`;

// "125 additional free videos (50 hours)": hours as stats.json has them, at least 1
export function getAmount({count, hours}, adjectives = "") {
  const noun = [adjectives, count === 1 ? "video" : "videos"].filter(Boolean).join(" ");
  return `${count} ${noun} (${getPlural(Math.max(1, hours), "hour")})`;
}

// "all Workshop videos up to 2024 and all Masterclass videos up to 2023": only what the account
// has (videoAccess.workshopVideosUntil, masterclassVideosUntil); "" when it has neither
export function getEntitlementText(videoAccess = {}) {
  const parts = [];
  const workshop = getUntil(videoAccess, "workshop");
  const masterclass = getUntil(videoAccess, "masterclass");
  if (workshop) parts.push(`all Workshop videos up to ${workshop}`);
  if (masterclass) parts.push(`all Masterclass videos up to ${masterclass}`);
  return parts.join(" and ");
}
